// Last-line-of-defense reload when a dynamic import fails — covers any case
// the Task 1 reload-on-update path missed (mobile throttling, race between
// SW activation and a click-triggered lazy import, etc).
//
// Time-based guard (not a one-shot boolean): a real outage might trigger
// many chunk errors in succession; we cap reloads at one per 60 s so we
// never tight-loop, but a NEW failure 5 minutes later still recovers.

// Explicit import so the file type-checks before .nuxt/ is regenerated
// (other plugins rely on the auto-import, but we want this one bulletproof).
import { defineNuxtPlugin } from "#imports";

const RELOAD_TIME_KEY = "refueat:last-chunk-reload";
const MIN_INTERVAL_MS = 60_000;

// `Loading chunk` alone is too loose (it can appear in unrelated UI strings
// and progress logs); the canonical webpack/Vite form is "Loading chunk N
// failed." — anchor on the failure verb.
const LOADING_CHUNK_FAILED_RE = /Loading chunk \S+ failed/;

function chunkErrorMatch(message: string): boolean {
    // Patterns observed across Chrome/Safari/Firefox for failed dynamic imports.
    return (
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("error loading dynamically imported module") ||
        message.includes("Importing a module script failed") ||
        LOADING_CHUNK_FAILED_RE.test(message) ||
        message.includes("ChunkLoadError") ||
        message.includes("Unable to preload CSS")
    );
}

function shouldReload(reason: string): boolean {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_TIME_KEY) || 0);
        const elapsed = Date.now() - last;
        if (elapsed < MIN_INTERVAL_MS) {
            console.warn(
                `[chunk-error-reload] suppressed (${reason}) — last reload ${Math.round(elapsed / 1000)}s ago, min ${MIN_INTERVAL_MS / 1000}s`,
            );
            return false;
        }
        return true;
    } catch {
        // sessionStorage unavailable (private mode quirks) — allow reload.
        return true;
    }
}

function attemptReload(reason: string) {
    if (typeof window === "undefined") return;
    if (!shouldReload(reason)) return;
    // Set the timestamp BEFORE reload(): worst case a cancelled reload
    // (extension, beforeunload) blocks the next attempt for 60 s — that's
    // preferable to risking a tight loop on a genuinely broken build.
    try {
        sessionStorage.setItem(RELOAD_TIME_KEY, String(Date.now()));
    } catch {
        // ignore
    }
    console.warn(`[chunk-error-reload] reloading: ${reason}`);
    window.location.reload();
}

export default defineNuxtPlugin((nuxtApp) => {
    if (typeof window === "undefined") return;

    // 1. Vite's first-class preload-error event.
    window.addEventListener("vite:preloadError", (event: Event) => {
        const detail = (event as CustomEvent).detail;
        const msg = detail?.message ?? String(detail ?? "preload error");
        attemptReload(`vite:preloadError ${msg}`);
    });

    // 2. Promise rejections that didn't reach Vue (e.g. user code awaiting
    //    an import() outside a component).
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        const message =
            (reason && typeof reason === "object" && "message" in reason
                ? String(reason.message)
                : String(reason ?? "")) || "";
        if (chunkErrorMatch(message)) {
            attemptReload(`unhandledrejection: ${message}`);
        }
    });

    // 3. Script-tag load failures and uncaught script errors. Filter to
    //    chunk-shaped sources so we don't reload on unrelated runtime errors.
    window.addEventListener(
        "error",
        (event) => {
            const filename = event.filename ?? "";
            const message = event.message ?? "";
            const isChunkPath = /\/_nuxt\//.test(filename);
            if (isChunkPath || chunkErrorMatch(message)) {
                attemptReload(
                    `window.error ${event.message} @ ${event.filename}`,
                );
            }
        },
        true, // capture, so we see it even if a frame swallows it
    );

    // 4. Vue/Nuxt-caught errors that never reach window-level handlers.
    //
    // Wrap any previously-registered handler so we don't silently shadow
    // it. If a future plugin sets vueApp.config.errorHandler AFTER us, the
    // shadowing direction would flip — but the intent here is clear:
    // chunk errors trigger a reload, anything else falls through.
    const prevErrorHandler = nuxtApp.vueApp.config.errorHandler;
    nuxtApp.vueApp.config.errorHandler = (err, instance, info) => {
        const message =
            err && typeof err === "object" && "message" in err
                ? String((err as { message: string }).message)
                : String(err ?? "");
        if (chunkErrorMatch(message)) {
            attemptReload(`vueApp.errorHandler: ${message}`);
            return;
        }
        if (prevErrorHandler) {
            prevErrorHandler(err, instance, info);
        } else {
            console.error("[vue:errorHandler]", err);
        }
    };
    nuxtApp.hook("vue:error", (err) => {
        const message =
            err && typeof err === "object" && "message" in err
                ? String((err as { message: string }).message)
                : String(err ?? "");
        if (chunkErrorMatch(message)) {
            attemptReload(`nuxt vue:error hook: ${message}`);
        }
    });
});
